import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@material-ui/core';
import { CheckCircleOutline, Close, CloudUploadOutlined, ErrorOutline, Warning } from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import { CollapsibleRow, ConfirmDialog, WrappedTableCell } from '@sierralabs/core-ui';
import { TaskTypePicker, useFindTaskTypes } from '@sierralabs/workflow-ui';
const StyledBox = styled(Box) `
  .inline-icon {
    vertical-align: middle;
    margin-left: 4px;
    margin-right: 4px;
  }
`;
const StyledCollapsibleRow = styled(CollapsibleRow) `
  .secondary {
    color: ${props => props.theme.palette.action.active};
  }
  .success-icon {
    color: ${props => props.theme.palette.success.main};
  }
  .error-icon {
    color: ${props => props.theme.palette.error.main};
  }
  .warning-icon {
    color: ${props => props.theme.palette.warning.main};
  }
  .progress {
    position: relative;
    margin-left: 14px;
    .bg-progress {
      color: ${props => props.theme.palette.primary.light};
    }
    .fg-progress {
      position: absolute;
      left: 0;
    }
  }
`;
export const FileUploadList = props => {
    const { folder, manager, uploader, referenceTaskId, renderActions } = props;
    const itemsRef = useRef([]);
    const [removeItem, setRemoveItem] = useState();
    const [selectedId, setSelectedId] = useState();
    const [commonTaskType, setCommonTaskType] = useState();
    const { data: taskTypes = [], isLoading, error, findTaskTypes } = useFindTaskTypes();
    const { progress } = manager;
    const severity = useMemo(() => {
        const { total, success, error } = progress;
        if (total === success) {
            return 'success';
        }
        else if (total === error) {
            return 'error';
        }
        else if (error > 0) {
            return 'warning';
        }
        else {
            return 'info';
        }
    }, [progress]);
    const handleRef = (ref, file) => {
        const index = itemsRef.current.findIndex(item => item.file === file);
        // if ref exists, save if it doesn't exist
        // otherwise, remove it if it exists
        if (ref) {
            if (index === -1) {
                itemsRef.current.push(ref);
            }
            else {
                itemsRef.current.splice(index, 1, ref);
            }
        }
        else if (index > -1) {
            itemsRef.current.splice(index, 1);
        }
    };
    const cancelAll = useCallback(() => uploader.clear(true), [uploader]);
    const uploadAll = useCallback(() => {
        const files = itemsRef.current.map(item => item.file);
        uploader.upload(files, (file, presignedUrl) => {
            // assert that taskType exists
            const item = itemsRef.current.find(item => item.file === file);
            return {
                name: file.name,
                size: file.size,
                mimeType: file.type,
                parentId: folder.id,
                referenceTaskId,
                taskTypeId: (item === null || item === void 0 ? void 0 : item.taskType.id) || 0,
                storageKey: presignedUrl.storageKey
            };
        });
    }, [folder.id, referenceTaskId, uploader]);
    useEffect(() => {
        findTaskTypes({ entityTable: 'file' });
    }, [findTaskTypes]);
    const uploadAllButton = useMemo(() => {
        const { pending } = progress;
        return pending <= 0 ? null : (React.createElement(Button, { color: 'primary', variant: 'contained', onClick: uploadAll, endIcon: React.createElement(CloudUploadOutlined, null) }, pending === 1 ? 'Upload File' : `Upload All ${pending} Files`));
    }, [progress, uploadAll]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { mx: 2, mb: 1 },
            React.createElement(TaskTypePicker, { options: taskTypes, textFieldProps: {
                    label: 'Select Document Type for all items',
                    placeholder: 'Choose a Document Type'
                }, onChange: taskType => setCommonTaskType(taskType || undefined) })),
        React.createElement(TableContainer, null,
            React.createElement(Table, null,
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, null, "File Name"),
                        React.createElement(TableCell, null, "Document Type"),
                        React.createElement(TableCell, null, "Progress"))),
                React.createElement(TableBody, null, manager.items.map(item => (React.createElement(FileUploadListItem, { key: item.id, item: item, folder: folder, uploader: uploader, taskType: commonTaskType, open: selectedId === item.id, referenceTaskId: referenceTaskId, selectorProps: {
                        isLoading,
                        options: taskTypes,
                        errorMessage: error === null || error === void 0 ? void 0 : error.message
                    }, innerRef: ref => handleRef(ref, item.file), setOpen: open => setSelectedId(open ? item.id : undefined), onRemove: () => setRemoveItem(item), onError: () => manager.setStatus(item.id, 'error'), onCreate: () => manager.setStatus(item.id, 'success'), onLoad: isLoading => {
                        if (isLoading && item.status === 'pending') {
                            manager.setStatus(item.id, 'uploading');
                        }
                    }, onIncomplete: isIncomplete => {
                        // set as pending if currently incomplete
                        const status = isIncomplete
                            ? 'incomplete'
                            : item.status === 'incomplete'
                                ? 'pending'
                                : undefined;
                        status && manager.setStatus(item.id, status);
                        status === 'pending' && uploader.remove(item.file);
                    } })))))),
        React.createElement(StyledBox, { mx: 3, mt: 1 }, progress.total === progress.pending + progress.incomplete ? (React.createElement(Alert, { severity: 'info' },
            "Click on the upload icon",
            ' ',
            React.createElement(CloudUploadOutlined, { color: 'primary', className: 'inline-icon' }),
            " to start and confirm the files for uploading.")) : (React.createElement(Alert, { severity: severity },
            React.createElement(AlertTitle, null,
                "Uploading ",
                React.createElement("strong", null, progress.total),
                " files to",
                ' ',
                folder.id === null ? (React.createElement(React.Fragment, null,
                    "the ",
                    React.createElement("strong", null, "root"),
                    " folder.")) : (React.createElement(React.Fragment, null,
                    React.createElement("strong", null, folder.name || '?'),
                    "."))),
            "Progress: ",
            React.createElement("strong", null, progress.success),
            " uploaded.",
            ' ',
            React.createElement("strong", null, progress.error),
            " failed.",
            ' ',
            React.createElement("strong", null, progress.uploading),
            " uploading.",
            ' ',
            React.createElement("strong", null, progress.pending),
            " pending.",
            ' ',
            React.createElement("strong", null, progress.incomplete),
            " incomplete."))), renderActions === null || renderActions === void 0 ? void 0 :
        renderActions({ progress, uploadAllButton, cancelAll, uploadAll }),
        React.createElement(ConfirmDialog, { open: !!removeItem, title: 'Remove File', message: `Are you sure you want to remove the file "${(removeItem === null || removeItem === void 0 ? void 0 : removeItem.file.name) || '?'}" from upload?`, onCancel: () => setRemoveItem(undefined), onConfirm: () => {
                setRemoveItem(undefined);
                if (removeItem) {
                    manager.remove(removeItem.id);
                }
            } })));
};
export default FileUploadList;
export const FileUploadListItem = props => {
    const { open, item, folder, uploader, selectorProps, referenceTaskId, taskType: commonTaskType, innerRef, setOpen, onLoad, onError, onCreate, onRemove, onIncomplete } = props;
    const { file } = item;
    const [taskType, setTaskType] = useState();
    const [alertMessage, setAlertMessage] = useState();
    const { options: taskTypes } = selectorProps || {};
    const uploadItem = useMemo(() => uploader.find(file), [file, uploader]);
    const error = uploadItem === null || uploadItem === void 0 ? void 0 : uploadItem.error;
    const newFile = uploadItem === null || uploadItem === void 0 ? void 0 : uploadItem.fileDto;
    const progress = (uploadItem === null || uploadItem === void 0 ? void 0 : uploadItem.progress) || 0;
    const isUploading = useMemo(() => {
        return (!!uploadItem &&
            ['loading', 'uploading', 'creating'].includes(uploadItem.status));
    }, [uploadItem]);
    const doUpload = () => {
        if (!taskType) {
            return;
        }
        uploader.upload([file], (file, presignedUrl) => {
            return {
                name: file.name,
                size: file.size,
                mimeType: file.type,
                parentId: folder.id,
                referenceTaskId,
                taskTypeId: taskType.id,
                storageKey: presignedUrl.storageKey
            };
        });
    };
    useEffect(() => {
        innerRef === null || innerRef === void 0 ? void 0 : innerRef(taskType ? { file, taskType } : null);
    }, [file, taskType, innerRef]);
    // clear innerRef on unmount
    useEffect(() => {
        return () => innerRef === null || innerRef === void 0 ? void 0 : innerRef(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // set first available File TaskType
    useEffect(() => {
        if (taskTypes === null || taskTypes === void 0 ? void 0 : taskTypes[0]) {
            setTaskType(taskTypes[0]);
        }
    }, [taskTypes]);
    useEffect(() => {
        // set selected TaskType of parent selector
        if (!newFile && commonTaskType) {
            setTaskType(commonTaskType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commonTaskType]);
    useEffect(() => {
        // if no TaskType, set as incomplete
        onIncomplete === null || onIncomplete === void 0 ? void 0 : onIncomplete(!taskType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskType]);
    useEffect(() => {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad(isUploading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUploading]);
    useEffect(() => {
        if (newFile) {
            setAlertMessage({
                severity: 'success',
                message: `Document "${newFile.name}" created.`
            });
            onCreate === null || onCreate === void 0 ? void 0 : onCreate(newFile);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newFile]);
    useEffect(() => {
        if (error) {
            setAlertMessage({
                severity: 'error',
                message: error.message || 'Unable to upload document.'
            });
            onError === null || onError === void 0 ? void 0 : onError(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);
    useEffect(() => {
        // reset state when changing back to pending
        if (item.status === 'pending') {
            setAlertMessage(undefined);
        }
    }, [item.status]);
    const collapsedContent = (React.createElement(Box, { padding: 2 },
        React.createElement(Box, null,
            React.createElement(TaskTypePicker, Object.assign({}, selectorProps, { disabled: !!newFile, value: taskType === null || taskType === void 0 ? void 0 : taskType.id, textFieldProps: {
                    label: 'Select Document Type',
                    placeholder: 'Choose a Document Type'
                }, onChange: taskType => setTaskType(taskType || undefined) }))),
        alertMessage && (React.createElement(Box, { mt: 2 },
            React.createElement(Alert, { severity: alertMessage.severity }, alertMessage.message)))));
    return (React.createElement(StyledCollapsibleRow, { hideAction: true, open: open, colSpan: 3, collapsedContent: collapsedContent, rowProps: { hover: true, onClick: () => setOpen(!open) } },
        React.createElement(WrappedTableCell, null, file.name),
        React.createElement(WrappedTableCell, { title: 'Click to update', className: !taskType ? 'secondary' : undefined }, taskType
            ? `${taskType.label} (${taskType.prefix})`
            : 'Click to update'),
        React.createElement(TableCell, { className: 'actions' }, newFile || error ? (React.createElement(IconButton, { onClick: event => {
                event.stopPropagation();
                setOpen(!open);
            } }, error ? (React.createElement(ErrorOutline, { className: 'error-icon' })) : (React.createElement(CheckCircleOutline, { className: 'success-icon' })))) : !isUploading ? (React.createElement(React.Fragment, null,
            !taskType ? (React.createElement(Tooltip, { title: 'Missing Document Type' },
                React.createElement(IconButton, null,
                    React.createElement(Warning, { className: 'warning-icon' })))) : (React.createElement(IconButton, { color: 'primary', disabled: !taskType, onClick: event => {
                    event.stopPropagation();
                    doUpload();
                } },
                React.createElement(CloudUploadOutlined, null))),
            React.createElement(IconButton, { onClick: event => {
                    event.stopPropagation();
                    onRemove === null || onRemove === void 0 ? void 0 : onRemove();
                } },
                React.createElement(Close, null)))) : (React.createElement("span", { className: 'progress' },
            React.createElement(CircularProgress, { size: 20, value: 100, thickness: 6, variant: 'determinate', className: 'bg-progress' }),
            React.createElement(CircularProgress, { size: 20, thickness: 6, value: progress, className: 'fg-progress', variant: !progress || progress === 100 ? 'indeterminate' : 'determinate' }))))));
};
//# sourceMappingURL=FileUploadList.js.map